import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";

function useIsMobile(maxWidth = 768) {
    const [isMobile, setIsMobile] = useState(() => (
        typeof window !== "undefined" ? window.innerWidth <= maxWidth : true
    ));

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= maxWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [maxWidth]);

    return isMobile;
}

export default function ToolbarSearch({
    value = "",
    onChange,
    placeholder = "Пошук...",
    title = "Пошук",
}) {
    const [open, setOpen] = useState(() => Boolean(value));
    const isMobile = useIsMobile(768);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const fieldRef = useRef(null);

    useEffect(() => {
        if (value) setOpen(true);
    }, [value]);

    useEffect(() => {
        if (!open) return undefined;
        const id = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocDown = (event) => {
            const inTrigger = wrapRef.current?.contains(event.target);
            const inField = fieldRef.current?.contains(event.target);
            if (!inTrigger && !inField && !value) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                if (value && onChange) onChange("");
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("touchstart", onDocDown, { passive: true });
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("touchstart", onDocDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open, value, onChange]);

    const handleClear = () => {
        if (onChange) onChange("");
        inputRef.current?.focus();
    };

    const openSearch = (event) => {
        event.currentTarget.blur();
        setOpen(true);
    };

    const searchField = (
        <div className="toolbar-search__field" ref={fieldRef}>
            <Search size={15} className="toolbar-search__glyph" aria-hidden="true" />
            <input
                ref={inputRef}
                type="text"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                className="toolbar-search__input"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                aria-label={title}
            />
            <button
                type="button"
                className="toolbar-search__clear"
                onClick={() => {
                    if (value) {
                        handleClear();
                        return;
                    }
                    setOpen(false);
                }}
                aria-label={value ? "Очистити пошук" : "Закрити пошук"}
                title={value ? "Очистити" : "Закрити"}
            >
                <X size={14} />
            </button>
        </div>
    );

    return (
        <div className={`toolbar-search ${open ? "is-open" : ""} ${value ? "has-value" : ""}`} ref={wrapRef}>
            {!open ? (
                <button
                    type="button"
                    className="add-btn refresh toolbar-icon-btn"
                    onClick={openSearch}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    title={title}
                    aria-label={title}
                >
                    <Search size={16} />
                </button>
            ) : isMobile ? (
                <>
                    <button
                        type="button"
                        className="add-btn refresh toolbar-icon-btn is-search-open"
                        onClick={() => setOpen(false)}
                        onPointerUp={(e) => e.currentTarget.blur()}
                        title="Закрити пошук"
                        aria-label="Закрити пошук"
                        aria-pressed="true"
                    >
                        <Search size={16} />
                    </button>
                    {typeof document !== "undefined"
                        ? createPortal(
                            <div className="toolbar-search-portal" role="search">
                                {searchField}
                            </div>,
                            document.body
                        )
                        : null}
                </>
            ) : (
                searchField
            )}
        </div>
    );
}
